import { Warning } from "@mui/icons-material";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import SearchSharpIcon from "@mui/icons-material/SearchSharp";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Theme,
  Typography,
} from "@mui/material";
import { blue } from "@mui/material/colors";
import { makeStyles } from "@mui/styles";
import { area } from "@turf/turf";
import {
  useBoundaryFeatures,
  isGeometryTooBig,
  isTooManyFeatures,
  GEOMETRY_SIZE_LIMIT_KB,
} from "components/boundary";
import { zoomToFeature } from "pages/geoscape-data/explorer/utils";
import React, { useCallback, useState } from "react";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { BoundaryComponent } from "./boundary-component";
import { BoundaryItem } from "./boundary-item";
import { DrawComponent } from "./draw-component";
import { UploadComponent } from "./upload-component";

export type FeatureOrFeatureArray =
  | GeoJSON.Feature<GeoJSON.Geometry>
  | GeoJSON.Feature<GeoJSON.Geometry>[];

export interface BoundaryToolProps {
  selectedFeature: string | null;
  setSelectedFeature: React.Dispatch<React.SetStateAction<string | null>>;
  addOrUpdateFeatures: (feat: FeatureOrFeatureArray) => void;
  currentMode: string;
  setCurrentMode: React.Dispatch<React.SetStateAction<string>>;
}

const boundarySteps = [
  { stepName: "Upload", element: UploadComponent },
  { stepName: "Draw", element: DrawComponent },
  { stepName: "Boundary", element: BoundaryComponent },
];

const calculateTotalArea = (
  features: GeoJSON.Feature<GeoJSON.Geometry>[]
): number => {
  return features.reduce(
    (sum, feature) => sum + area(feature),
    0
  );
};

export const DefineBoundary = (
  props: ClipConfigProps & ClipStepsControlProps
) => {
  const classes = useStyles();
  const [component, setComponent] = useState("Draw");

  const onGeometryChange = useCallback(
    (geometry: GeoJSON.FeatureCollection | undefined) => {
      props.setClipConfig((prev) => ({
        ...prev,
        geometry: geometry ?? { type: "FeatureCollection", features: [] },
      }));
    },
    [props.setClipConfig]
  );

  const {
    features,
    selectedFeatureId,
    currentMode,
    setCurrentMode,
    addOrUpdateFeatures,
    handleDelete,
    handleRename,
    handleSelect: baseHandleSelect,
  } = useBoundaryFeatures({
    draw: props.draw,
    mapRef: props.mapRef as React.RefObject<any>,
    initialGeometry: props.clipConfig.geometry,
    onGeometryChange,
  });

  // Extend select to also zoom to feature
  const handleSelectSidebar = useCallback(
    (id: string) => {
      baseHandleSelect(id);
      const selectedFeature = features.find((x) => x.id === id);
      if (selectedFeature) {
        zoomToFeature(props.mapRef, selectedFeature.geometry);
      }
    },
    [baseHandleSelect, features, props.mapRef]
  );

  const isConfigTooBig = isGeometryTooBig(features);
  const tooManyFeatures = isTooManyFeatures(features);
  const isAreaTooBig = calculateTotalArea(features) / 1000000 > 100;

  return (
    <Box display="flex" flexDirection="column" gap={1} height="100%">
      <Typography variant="h5">Define Clip Boundary</Typography>
      <Box
        display="flex"
        flexDirection="row"
        gap={1}
        justifyContent="space-between"
      >
        <Card
          className={
            component === "Upload" ? classes.typecardSelected : classes.typecard
          }
          variant="outlined"
        >
          <CardActionArea
            data-cy="upload"
            onClick={() => setComponent("Upload")}
          >
            <CardContent className={classes.typecard}>
              <Avatar sx={{ bgcolor: blue[50] }}>
                <UploadFileIcon color="info" />
              </Avatar>
              <Typography variant="subtitle1">Upload</Typography>
              <Typography variant="body2" color="text.secondary">
                ZIP(10MB)
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card
          sx={{ padding: "0px" }}
          className={
            component === "Draw" ? classes.typecardSelected : classes.typecard
          }
          variant="outlined"
        >
          <CardActionArea onClick={() => setComponent("Draw")}>
            <CardContent className={classes.typecard}>
              <Avatar sx={{ bgcolor: blue[50] }}>
                <EditSharpIcon color="info" />
              </Avatar>
              <Typography variant="subtitle1">Draw</Typography>
              <Typography variant="body2" color="text.secondary">
                Boundary
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
        <Card
          sx={{ padding: "0px" }}
          className={classes.typecardDisabled}
          variant="outlined"
        >
          <CardActionArea onClick={() => setComponent("Boundary")}>
            <CardContent className={classes.typecardDisabled}>
              <Avatar className={classes.disabled}>
                <SearchSharpIcon className={classes.disabled} />
              </Avatar>
              <Typography variant="subtitle1" className={classes.disabled}>
                Boundary
              </Typography>
              <Typography variant="body2" className={classes.disabled}>
                Search
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
      <Box>
        {React.createElement(
          boundarySteps.filter((b) => b.stepName === component)[0].element,
          {
            selectedFeature: selectedFeatureId,
            setSelectedFeature: () => {},
            addOrUpdateFeatures,
            currentMode,
            setCurrentMode,
            ...props,
          }
        )}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        gap={1}
        overflow="auto"
        flex="1"
        minHeight={0}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          {features.map((feature, idx) => (
            <BoundaryItem
              key={feature.id as string}
              id={feature.id as string}
              selected={feature.id == selectedFeatureId}
              name={feature.properties?.name ?? `Boundaries ${idx}`}
              area={area(feature)}
              onDelete={handleDelete}
              onEdit={handleRename}
              onSelect={handleSelectSidebar}
            />
          ))}
        </Box>
      </Box>
      {isConfigTooBig && (
        <Alert icon={<Warning />} color="warning">
          Geometry exceeds {GEOMETRY_SIZE_LIMIT_KB}kb, remove some features to
          continue
        </Alert>
      )}
      {tooManyFeatures && (
        <Alert icon={<Warning />} color="warning">
          Number of features exceeds the maximum of 20, remove{" "}
          {features.length - 20} to continue
        </Alert>
      )}
      {isAreaTooBig && (
        <Alert icon={<Warning />} color="info" variant="outlined">
          For large boundaries a commercial license could be more suitable.
        </Alert>
      )}
      <Box alignSelf="flex-end" marginTop="auto" display="flex">
        <Button
          disabled={
            features.length === 0 || isConfigTooBig || tooManyFeatures
          }
          onClick={() => {
            props.setActiveStep(
              Math.min(props.activeStep + 1, props.maxSteps)
            );
          }}
        >
          Confirm
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  typecard: {
    width: "100px",
    height: "136px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    "&:hover": {
      background: theme.palette.action.hover,
    },
  },
  typecardSelected: {
    width: "100px",
    height: "136px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    background: theme.palette.action.selected,
  },
  typecardDisabled: {
    width: "100px",
    height: "136px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    pointerEvents: "none",
  },
  disabled: {
    color: theme.palette.action.disabled,
  },
}));
